import { useEffect, useRef, useState } from 'react'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { streamApplicationStatus } from '@/lib/api'

export interface ApplicationStatusStreamState {
  state: string
  stateMessage: string
  progressPercent: number
  updatedAt?: Timestamp
}

export function useApplicationStatusStream(appId: string, options?: { enabled?: boolean }) {
  const [status, setStatus] = useState<ApplicationStatusStreamState | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const retryTimeoutRef = useRef<number | null>(null)
  const controllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!appId || options?.enabled === false) {
      return
    }

    const connect = async () => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      setIsConnected(true)
      setError(null)

      try {
        for await (const response of streamApplicationStatus(appId, { signal: controller.signal })) {
          setStatus({
            state: response.state,
            stateMessage: response.stateMessage,
            progressPercent: response.progressPercent ?? 0,
            updatedAt: response.stateUpdatedAt ?? undefined,
          })
        }
        setIsConnected(false)
      } catch (err) {
        if (!controller.signal.aborted) {
          setIsConnected(false)
          setError(err instanceof Error ? err : new Error('Failed to stream application status'))
          retryTimeoutRef.current = window.setTimeout(() => {
            connect()
          }, 5000)
        }
      }
    }

    connect()

    return () => {
      controllerRef.current?.abort()
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current)
      }
      setIsConnected(false)
    }
  }, [appId, options?.enabled])

  return { status, isConnected, error }
}
